import React, { useState, useContext, useEffect } from "react";

import { authBackend } from "./backend";
import { getRefreshToken, setRefreshToken, setAccessToken } from "./utils";
import AuthContext from "./AuthContext";
import { Link } from "react-router-dom";

import validator from "validator";
import Loading from "./components/Loading";

const logo = `${process.env.PUBLIC_URL}/logos/binimeliz-erp-logo.png`;

const Login = () => {
  useEffect(() => {
    document.title = "Ingreso";
  }, []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [hasCapsLock, setHasCapsLock] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const authContext = useContext(AuthContext);

  const checkLogged = async () => {
    const refreshToken = getRefreshToken();
    try {
      if (refreshToken) {
        let response = await authBackend.post(
          "/user/logged",
          { refreshToken, userAgent: navigator.userAgent },
          { timeout: 5000 }
        );
        authContext.user = response.data;
        authContext.login();
      } else {
        setIsLoading(false);
      }
    } catch (err) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkLogged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    setError("");
    if (!email || !password) {
      setError("Faltan campos por completar");
      return;
    }
    if (!validator.isEmail(email)) {
      setError("Correo no válido");
      return;
    }
    setSubmitting(true);
    try {
      let response = await authBackend.post("/user/login", { email, password });
      const token = response.data.token;
      const userAgent = navigator.userAgent;

      response = await authBackend.post("/user/validate", { userAgent, token });

      authContext.user = response.data.user;
      setAccessToken(response.data.accessToken);
      setRefreshToken(response.data.refreshToken);

      authContext.login();
    } catch (err) {
      console.log(err);
      const status = err?.response?.status;
      if (status === 429) {
        setError("Demasiados intentos de ingreso. Espere unos minutos y vuelva a intentar.");
      } else if (!err?.response || status >= 500) {
        setError("No se pudo conectar con el servidor. Intente nuevamente.");
      } else {
        setError("Correo o contraseña inválidos");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) return <Loading />;

  return (
    <div style={{
      position: "fixed", inset: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      // Ambiente del logo ERP: glow rojo sobre negro industrial.
      background: "radial-gradient(circle at 50% 38%, #3a0e0e 0%, #1c1110 42%, #0c0c0c 100%)",
      padding: 20,
      fontFamily: "Arial, sans-serif",
    }}>
      <div style={{
        background: "rgba(26,22,22,0.92)",
        border: "1px solid #3a2222",
        borderRadius: 12,
        padding: "30px 36px 32px",
        width: "100%",
        maxWidth: 460,
        boxShadow: "0 16px 50px rgba(0,0,0,0.6), 0 0 60px rgba(227,6,19,0.10)",
      }}>
        <div style={{ textAlign: "center", marginBottom: 18 }}>
          <img src={logo} alt="Binimeliz ERP" style={{ width: 360, maxWidth: "100%", height: "auto" }} />
        </div>

        <div style={{ fontSize: 12, color: "#b9a3a3", textAlign: "center", marginBottom: 22, letterSpacing: ".04em" }}>
          Inicia sesión para continuar
        </div>

        <form
          onSubmit={e => { e.preventDefault(); submit(); }}
          autoComplete="on"
        >
          <div style={{ marginBottom: 12 }}>
            <label style={S.label}>Correo electrónico</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="ejemplo@gmail.com"
              autoComplete="email"
              required
              style={S.input}
            />
          </div>

          <div style={{ marginBottom: 4 }}>
            <label style={{ ...S.label, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Contraseña</span>
              <Link to="/forgot" style={{ fontSize: 10, color: "#3498db", textDecoration: "none" }}>
                ¿olvidaste tu contraseña?
              </Link>
            </label>
            <div style={{ position: "relative" }}>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => {
                  setHasCapsLock(e.getModifierState("CapsLock"));
                  if (e.key === "Enter") submit();
                }}
                placeholder="••••••••••••"
                autoComplete="current-password"
                autoCapitalize="off"
                required
                style={{ ...S.input, paddingRight: 40 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                tabIndex={-1}
                style={{
                  position: "absolute", right: 4, top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent", border: "none", cursor: "pointer",
                  color: "#9aa3ab", padding: 6, lineHeight: 0,
                  display: "flex", alignItems: "center",
                }}
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19M6.61 6.61A18.45 18.45 0 0 0 2 12s3 8 10 8a9.12 9.12 0 0 0 5.39-1.61" />
                    <path d="M14.12 14.12A3 3 0 1 1 9.88 9.88" />
                    <path d="M1 1l22 22" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            {hasCapsLock && (
              <div style={{ fontSize: 10, color: "#f39c12", marginTop: 4 }}>
                ⚠ Tienes las mayúsculas activas
              </div>
            )}
          </div>

          {error && (
            <div style={{
              background: "#2d0000", border: "1px solid #4a0000",
              color: "#e74c3c", padding: "7px 10px", borderRadius: 5,
              fontSize: 11, marginTop: 12,
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: "100%", marginTop: 18,
              background: "#e30613", color: "#fff",
              border: "none", borderRadius: 6,
              padding: "10px 20px",
              fontSize: 13, fontWeight: 700, cursor: "pointer",
              opacity: submitting ? 0.7 : 1,
              transition: "opacity .15s",
            }}
          >
            {submitting ? "Ingresando…" : "Acceder"}
          </button>
        </form>

        <div style={{
          marginTop: 24, paddingTop: 16,
          borderTop: "1px solid #3a2222",
          fontSize: 9, color: "#8a7676", textAlign: "center", letterSpacing: ".06em",
        }}>
          BINIMELIZ ERP · MECÁNICA DE PRECISIÓN
        </div>
      </div>
    </div>
  );
};

const S = {
  label: {
    display: "block",
    fontSize: 10, fontWeight: 700, color: "#cbd5e1",
    textTransform: "uppercase", letterSpacing: ".05em",
    marginBottom: 5,
  },
  input: {
    width: "100%",
    background: "#161616",
    border: "1px solid #333",
    borderRadius: 5,
    padding: "9px 12px",
    color: "#ddd",
    fontSize: 13,
    fontFamily: "Arial, sans-serif",
    boxSizing: "border-box",
    outline: "none",
  },
};

export default Login;
